import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import {
  Bell, Send, Clock, Users, Building2, UserRound, X, Trash2, Pencil, Ban, RotateCcw,
  Loader2, Search, CheckCircle, XCircle, AlertTriangle, Info, Smartphone, ChevronLeft, ChevronRight
} from 'lucide-react';
import { GET, POST, PUT, PATCH, DELETE } from '../services/api';

// Super-admin only: compose a custom push notification for all members, chosen
// chapters or chosen members — sent now or at a scheduled time — and manage the history.

const TITLE_MAX = 100;
const BODY_MAX = 500;

const TARGETS = [
  { value: 'all', label: 'All Members', icon: Users },
  { value: 'chapters', label: 'Chapters', icon: Building2 },
  { value: 'users', label: 'Selected Members', icon: UserRound },
];

const STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  sending: { label: 'Sending', cls: 'bg-blue-50 text-blue-700 border-blue-100' },
  sent: { label: 'Sent', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-100' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const STATUS_TABS = ['', 'scheduled', 'sent', 'failed', 'cancelled'];

const EMPTY_FORM = { title: '', body: '', target_type: 'all', chapters: [], members: [], when: 'now', scheduledAt: '' };

const toLocalInput = (d) => format(d, "yyyy-MM-dd'T'HH:mm");
const formatDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  const styles = {
    success: { bg: 'bg-emerald-600', icon: <CheckCircle className="w-5 h-5" /> },
    info: { bg: 'bg-blue-600', icon: <Info className="w-5 h-5" /> },
    error: { bg: 'bg-red-500', icon: <XCircle className="w-5 h-5" /> },
  };
  const s = styles[toast.severity] || styles.info;
  return (
    <div className={`fixed bottom-6 right-6 z-[100] max-w-sm px-5 py-3 rounded-xl shadow-2xl text-white flex items-center gap-3 ${s.bg}`}>
      {s.icon}
      <div className="flex-1 text-sm font-medium">{toast.message}</div>
      <button onClick={onClose} className="p-1 rounded-full hover:bg-white/20"><X className="w-4 h-4" /></button>
    </div>
  );
};

const ConfirmModal = ({ confirm, onCancel, onConfirm, busy }) => {
  if (!confirm) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-xl ${confirm.danger ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
            {confirm.danger ? <AlertTriangle className="w-5 h-5" /> : <Send className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">{confirm.title}</h3>
            <p className="text-sm text-slate-600 mt-1">{confirm.message}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} disabled={busy} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} className={`flex-1 px-4 py-2.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60 ${confirm.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {confirm.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Audience summary for a saved notification ("All members", "Krishna, Keshav", "3 members").
const audienceLabel = (n) => {
  if (n.target_type === 'all') return 'All members';
  if (n.target_type === 'chapters') {
    const names = (n.chapter_ids || []).map((c) => c?.name).filter(Boolean);
    return names.length ? names.join(', ') : `${(n.chapter_ids || []).length} chapter(s)`;
  }
  const names = (n.user_ids || []).map((u) => `${u?.first_name || ''} ${u?.last_name || ''}`.trim()).filter(Boolean);
  if (names.length <= 2) return names.join(', ') || `${(n.user_ids || []).length} member(s)`;
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

const PushNotifications = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chapterSearch, setChapterSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statusTab, setStatusTab] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total_records: 0, total_pages: 1 });

  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const composerRef = useRef(null);

  const showToast = useCallback((message, severity = 'info') => {
    setToast({ message, severity });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // --- Data -----------------------------------------------------------------
  useEffect(() => {
    GET('/users/chapters')
      .then((data) => setChapters(Array.isArray(data) ? data : data?.data || []))
      .catch(() => showToast('Failed to load chapters', 'error'));
  }, [showToast]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusTab) params.set('status', statusTab);
      const res = await GET(`/push-notifications?${params.toString()}`);
      setHistory(res.data || []);
      setPagination(res.pagination || { total_records: 0, total_pages: 1 });
    } catch (error) {
      showToast(error.message || 'Failed to load notifications', 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, [page, limit, statusTab, showToast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Scheduled items flip to sent/failed on the server — refresh while any are pending.
  useEffect(() => {
    if (!history.some((n) => n.status === 'scheduled' || n.status === 'sending')) return undefined;
    const id = setInterval(fetchHistory, 30000);
    return () => clearInterval(id);
  }, [history, fetchHistory]);

  // Member picker search (debounced)
  useEffect(() => {
    const term = memberSearch.trim();
    if (term.length < 2) { setMemberResults([]); return undefined; }
    const id = setTimeout(async () => {
      setSearchingMembers(true);
      try {
        const res = await GET(`/push-notifications/members?search=${encodeURIComponent(term)}`);
        setMemberResults(res.data || []);
      } catch {
        setMemberResults([]);
      } finally {
        setSearchingMembers(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [memberSearch]);

  // Recipient count for the current audience (debounced)
  const chapterIds = form.chapters.join(',');
  const memberIds = form.members.map((m) => m._id).join(',');
  useEffect(() => {
    const target = {
      target_type: form.target_type,
      chapter_ids: chapterIds ? chapterIds.split(',') : [],
      user_ids: memberIds ? memberIds.split(',') : [],
    };
    if ((target.target_type === 'chapters' && !target.chapter_ids.length) || (target.target_type === 'users' && !target.user_ids.length)) {
      setPreview(null);
      return undefined;
    }
    let active = true;
    const id = setTimeout(async () => {
      try {
        const res = await POST('/push-notifications/preview', target);
        if (active) setPreview(res.data);
      } catch {
        if (active) setPreview(null);
      }
    }, 300);
    return () => { active = false; clearTimeout(id); };
  }, [form.target_type, chapterIds, memberIds]);

  // --- Composer ---------------------------------------------------------------
  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); setMemberSearch(''); setChapterSearch(''); };

  const toggleChapter = (id) =>
    set({ chapters: form.chapters.includes(id) ? form.chapters.filter((c) => c !== id) : [...form.chapters, id] });

  const addMember = (m) => {
    if (!form.members.some((x) => x._id === m._id)) set({ members: [...form.members, m] });
    setMemberSearch('');
    setMemberResults([]);
  };

  // Load a saved notification into the composer: edit (scheduled) or reuse (any).
  const loadIntoComposer = (n, edit) => {
    setEditingId(edit ? n._id : null);
    setForm({
      title: n.title,
      body: n.body,
      target_type: n.target_type,
      chapters: (n.chapter_ids || []).map((c) => c?._id || c).filter(Boolean),
      members: (n.user_ids || []).filter((u) => u?._id).map((u) => ({ _id: u._id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() })),
      when: edit ? 'schedule' : 'now',
      scheduledAt: edit && n.scheduled_at ? toLocalInput(new Date(n.scheduled_at)) : '',
    });
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = () => {
    if (!form.title.trim()) return 'Enter a title';
    if (!form.body.trim()) return 'Enter a message';
    if (form.target_type === 'chapters' && !form.chapters.length) return 'Select at least one chapter';
    if (form.target_type === 'users' && !form.members.length) return 'Select at least one member';
    if (form.when === 'schedule') {
      if (!form.scheduledAt) return 'Pick a date and time';
      if (new Date(form.scheduledAt) <= new Date()) return 'Schedule time must be in the future';
    }
    return null;
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    body: form.body.trim(),
    target_type: form.target_type,
    chapter_ids: form.target_type === 'chapters' ? form.chapters : [],
    user_ids: form.target_type === 'users' ? form.members.map((m) => m._id) : [],
    // datetime-local is the admin's local time; send it as an absolute instant
    scheduled_at: form.when === 'schedule' ? new Date(form.scheduledAt).toISOString() : null,
  });

  const audienceText = () => {
    if (form.target_type === 'all') return 'all members';
    if (form.target_type === 'chapters') return `${form.chapters.length} chapter(s)`;
    return `${form.members.length} selected member(s)`;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) { showToast(error, 'error'); return; }
    const reach = preview ? ` It will reach ${preview.reachable} device(s).` : '';
    if (editingId) {
      setConfirm({ action: 'update', title: 'Update scheduled notification?', message: `It will be sent to ${audienceText()} on ${formatDateTime(form.scheduledAt)}.${reach}`, confirmLabel: 'Update' });
    } else if (form.when === 'schedule') {
      setConfirm({ action: 'create', title: 'Schedule this notification?', message: `It will be sent to ${audienceText()} on ${formatDateTime(form.scheduledAt)}.${reach}`, confirmLabel: 'Schedule' });
    } else {
      setConfirm({ action: 'create', title: 'Send this notification now?', message: `It goes out immediately to ${audienceText()} and can't be undone.${reach}`, confirmLabel: 'Send now' });
    }
  };

  const runConfirmed = async () => {
    const { action, item } = confirm;
    setSubmitting(true);
    try {
      if (action === 'create') {
        const res = await POST('/push-notifications', buildPayload());
        showToast(res.message || 'Done', 'success');
        resetForm();
      } else if (action === 'update') {
        await PUT(`/push-notifications/${editingId}`, buildPayload());
        showToast('Scheduled notification updated', 'success');
        resetForm();
      } else if (action === 'cancel') {
        await PATCH(`/push-notifications/${item._id}/cancel`, {});
        showToast('Scheduled notification cancelled', 'success');
        if (editingId === item._id) resetForm();
      } else if (action === 'delete') {
        await DELETE(`/push-notifications/${item._id}`);
        showToast('Notification deleted', 'success');
        if (editingId === item._id) resetForm();
      }
      setConfirm(null);
      fetchHistory();
    } catch (error) {
      showToast(error.message || 'Something went wrong', 'error');
      setConfirm(null);
      fetchHistory();
    } finally {
      setSubmitting(false);
    }
  };

  const filteredChapters = chapters.filter((c) => {
    const q = chapterSearch.trim().toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.city_id?.name?.toLowerCase().includes(q);
  });

  const inputClass = 'w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm text-slate-700';
  const labelClass = 'block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1';

  return (
    <div className="space-y-5">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <ConfirmModal confirm={confirm} busy={submitting} onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />

      {/* --- Composer --- */}
      <div ref={composerRef} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Bell className="w-5 h-5" /></div>
            <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">
              {editingId ? 'Edit Scheduled Notification' : 'New Push Notification'}
            </h2>
          </div>
          {(editingId || form.title || form.body) && (
            <button onClick={resetForm} className="text-sm font-semibold text-slate-500 hover:text-red-600 flex items-center gap-1">
              <X className="w-4 h-4" /> {editingId ? 'Cancel edit' : 'Clear'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            {/* Title + message */}
            <div>
              <label className={labelClass}>Title</label>
              <input value={form.title} maxLength={TITLE_MAX} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Chapter meeting moved to Friday" className={inputClass} />
              <p className="text-xs text-slate-400 mt-1 text-right">{form.title.length}/{TITLE_MAX}</p>
            </div>
            <div>
              <label className={labelClass}>Message</label>
              <textarea value={form.body} maxLength={BODY_MAX} rows={4} onChange={(e) => set({ body: e.target.value })} placeholder="Write the notification text members will see" className={`${inputClass} resize-y`} />
              <p className="text-xs text-slate-400 mt-1 text-right">{form.body.length}/{BODY_MAX}</p>
            </div>

            {/* Audience */}
            <div>
              <label className={labelClass}>Send to</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {TARGETS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => set({ target_type: value })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${form.target_type === value ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-100' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>

              {form.target_type === 'chapters' && (
                <div className="mt-3 border border-slate-200 rounded-xl">
                  <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400 ml-1" />
                    <input value={chapterSearch} onChange={(e) => setChapterSearch(e.target.value)} placeholder="Search chapter or city" className="flex-1 p-1.5 text-sm outline-none" />
                    <span className="text-xs font-semibold text-slate-500 pr-2">{form.chapters.length} selected</span>
                  </div>
                  <div className="max-h-56 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {filteredChapters.length === 0 && <p className="text-sm text-slate-400 p-2">No chapters found.</p>}
                    {filteredChapters.map((c) => (
                      <label key={c._id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${form.chapters.includes(c._id) ? 'bg-blue-50 text-blue-800' : 'hover:bg-slate-50 text-slate-700'}`}>
                        <input type="checkbox" checked={form.chapters.includes(c._id)} onChange={() => toggleChapter(c._id)} className="accent-blue-600" />
                        <span className="font-medium">{c.name}</span>
                        {c.city_id?.name && <span className="text-xs text-slate-400">· {c.city_id.name}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {form.target_type === 'users' && (
                <div className="mt-3">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search member by name, business or phone" className={`${inputClass} pl-9`} />
                    {searchingMembers && <Loader2 className="w-4 h-4 text-blue-500 animate-spin absolute right-3 top-3" />}
                    {memberResults.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                        {memberResults.map((m) => {
                          const picked = form.members.some((x) => x._id === m._id);
                          return (
                            <button key={m._id} type="button" disabled={picked} onClick={() => addMember(m)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 disabled:opacity-50 flex items-center justify-between gap-3 border-b border-slate-50 last:border-0">
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-slate-800 truncate">{m.name}</span>
                                <span className="block text-xs text-slate-500 truncate">{[m.business_name, m.chapter].filter(Boolean).join(' · ')}</span>
                              </span>
                              {m.has_app
                                ? <span className="text-[10px] font-bold uppercase text-emerald-600 shrink-0">{picked ? 'Added' : 'Has app'}</span>
                                : <span className="text-[10px] font-bold uppercase text-slate-400 shrink-0">No app</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {form.members.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {form.members.map((m) => (
                        <span key={m._id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-blue-50 text-blue-800 text-sm font-medium border border-blue-100">
                          {m.name}
                          <button onClick={() => set({ members: form.members.filter((x) => x._id !== m._id) })} className="p-0.5 rounded-full hover:bg-blue-100"><X className="w-3.5 h-3.5" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {preview && (
                <p className="text-sm text-slate-600 mt-3 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-blue-500" />
                  Reaches <span className="font-bold text-slate-800">{preview.reachable}</span> of {preview.members} member(s)
                  {preview.members > preview.reachable && <span className="text-slate-400">— the rest don't have the app or turned notifications off</span>}
                </p>
              )}
            </div>

            {/* When */}
            <div>
              <label className={labelClass}>When</label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {!editingId && (
                  <div className="flex gap-2">
                    {[{ v: 'now', label: 'Send now', icon: Send }, { v: 'schedule', label: 'Schedule', icon: Clock }].map(({ v, label, icon: Icon }) => (
                      <button key={v} type="button" onClick={() => set({ when: v })} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold ${form.when === v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}>
                        <Icon className="w-4 h-4" /> {label}
                      </button>
                    ))}
                  </div>
                )}
                {form.when === 'schedule' && (
                  <input type="datetime-local" value={form.scheduledAt} min={toLocalInput(new Date())} onChange={(e) => set({ scheduledAt: e.target.value })} className={`${inputClass} sm:max-w-xs`} />
                )}
              </div>
            </div>

            <button onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg shadow-blue-200 disabled:opacity-60">
              {editingId ? <><Pencil className="w-4 h-4" /> Update Schedule</> : form.when === 'schedule' ? <><Clock className="w-4 h-4" /> Schedule Notification</> : <><Send className="w-4 h-4" /> Send Notification</>}
            </button>
          </div>

          {/* Phone preview */}
          <div>
            <label className={labelClass}>Preview</label>
            <div className="rounded-3xl bg-slate-800 p-4">
              <div className="rounded-2xl bg-white/95 p-3 shadow-lg">
                <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1">
                  <Bell className="w-3.5 h-3.5 text-blue-600" /> <span className="font-semibold uppercase tracking-wide">ABN</span> <span>· now</span>
                </div>
                <p className="text-sm font-bold text-slate-900 break-words">{form.title || 'Notification title'}</p>
                <p className="text-sm text-slate-700 break-words whitespace-pre-wrap">{form.body || 'Your message will appear here.'}</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Members see this on their phone's lock screen. Scheduled notifications go out within a minute of the chosen time.</p>
          </div>
        </div>
      </div>

      {/* --- History --- */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Sent & Scheduled</h2>
          <div className="flex gap-1 overflow-x-auto">
            {STATUS_TABS.map((s) => (
              <button key={s || 'all'} onClick={() => { setStatusTab(s); setPage(1); }} className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${statusTab === s ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {s ? STATUS_META[s].label : 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                {['Notification', 'Audience', 'Status', 'Time', 'Delivered', 'Actions'].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingHistory && history.length === 0 ? (
                <tr><td colSpan="6" className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan="6" className="py-12 text-center text-slate-400 font-medium">No notifications yet.</td></tr>
              ) : history.map((n) => {
                const meta = STATUS_META[n.status] || STATUS_META.scheduled;
                return (
                  <tr key={n._id} className="hover:bg-slate-50/60 align-top">
                    <td className="px-5 py-3 max-w-xs">
                      <p className="text-sm font-bold text-slate-800 truncate" title={n.title}>{n.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-2 break-words" title={n.body}>{n.body}</p>
                      {n.error && n.status === 'failed' && <p className="text-xs text-red-500 mt-1">{n.error}</p>}
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600 max-w-[220px]"><span className="line-clamp-2">{audienceLabel(n)}</span></td>
                    <td className="px-5 py-3 whitespace-nowrap"><span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${meta.cls}`}>{meta.label}</span></td>
                    <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {n.status === 'scheduled'
                        ? <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber-500" /> {formatDateTime(n.scheduled_at)}</span>
                        : formatDateTime(n.sent_at || n.updatedAt)}
                    </td>
                    <td className="px-5 py-3 text-sm whitespace-nowrap">
                      {['sent', 'failed'].includes(n.status) && n.recipients_count > 0
                        ? <span><span className="font-bold text-emerald-600">{n.success_count}</span>{n.failure_count > 0 && <span className="text-red-500"> / {n.failure_count} failed</span>}<span className="text-slate-400"> · {n.recipients_count} members</span></span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {n.status === 'scheduled' && (
                          <>
                            <button title="Edit" onClick={() => loadIntoComposer(n, true)} className="p-2 rounded-lg text-amber-600 hover:bg-amber-50"><Pencil className="w-4 h-4" /></button>
                            <button title="Cancel schedule" onClick={() => setConfirm({ action: 'cancel', item: n, danger: true, title: 'Cancel this scheduled notification?', message: `"${n.title}" will not be sent.`, confirmLabel: 'Cancel schedule' })} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"><Ban className="w-4 h-4" /></button>
                          </>
                        )}
                        <button title="Use again" onClick={() => loadIntoComposer(n, false)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"><RotateCcw className="w-4 h-4" /></button>
                        {n.status !== 'sending' && (
                          <button title="Delete" onClick={() => setConfirm({ action: 'delete', item: n, danger: true, title: 'Delete this notification?', message: n.status === 'scheduled' ? 'It will be removed and will not be sent.' : 'It will be removed from this list. Members who already received it keep it.', confirmLabel: 'Delete' })} className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-medium">Rows:</span>
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 bg-white outline-none">
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
            <span>{pagination.total_records} total</span>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold text-slate-600">Page {page} of {Math.max(pagination.total_pages || 1, 1)}</span>
            <button disabled={page >= (pagination.total_pages || 1)} onClick={() => setPage(page + 1)} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotifications;
